/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { OnOffSwitch } from '@codex/basics';
import { EnvironmentThemeProvider } from '@codex-brightworld/themes';
import VideoSection from './video-section';
import { productSizingShowMeLink, productSizingUnits } from '../../../analytics';

const TO_MILLIMETERS = 1000;
const TO_INCHES = 39.37;

const ToggleMeasurement = styled(OnOffSwitch)`
    margin-right: 10px;
    div {
        background-color: #f5f7fd;
        border: 1px solid #4855a0;
        height: 28px;
        padding: 0px 10px 0px 22px;
        margin-right: 0;
        :before {
          background-color: #4855a0;
          border: 1px solid #4855a0;
          top: 1px;
          ${({ active }) => active && 'left: calc(100% - 27.5px);'}
        }
    }
`;
const ProductSizingContainer = styled.div`
    text-align: left;
    margin-bottom: 20px;
    a:hover {
      color: ${(props) => props.theme.colors.primary.purple};
    }
    border-radius: 10px;
    border: solid 1px ${(props) => props.theme.colors.neutral.grey[2]};
    width: auto;
    padding: 16px 16px 20px 2px;
}
`;
const SizeAndPackageTitle = styled.h4`
    height: 22px;
    font-family: ${(props) => props.theme.fonts.default.fontFamily};
    font-size: 18px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.22;
    letter-spacing: normal;
    color: ${(props) => props.theme.colors.neutral.currysBlack};
    margin: 0 -16px 15px -3px;
    padding: 0 15px 15px 15px;
    border-bottom: solid 1px ${(props) => props.theme.colors.neutral.grey[2]};
`;
const PackageDimensionLink = styled.a`
    width: 217px;
    height: 21px;
    margin: 32px 200px 11px 14px;
    font-family: ${(props) => props.theme.fonts.default.fontFamily};
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: ${(props) => props.theme.colors.primary.purple};
    @media ${(props) => props.theme.breakpoints.mobile} {
      margin: 32px 120px 11px 14px;
    }  

`;
const BoxsizeButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    @media ${(props) => props.theme.breakpoints.mobile} {
      padding-bottom: 30px;
    }
`;
const SizeLabel = styled.div`
    font-weight: ${({ isActive }) => (isActive ? 'bold' : 'normal')};
    letter-spacing: ${({ isActive }) => (isActive ? '0' : '0.56px')};
    margin-right: 8px;
    cursor: pointer;
`;
const PackageContentSection = styled.div`
    display: flex;
    justify-content: space-between;
    @media ${(props) => props.theme.breakpoints.mobile} {
        display: block;
    }
`;
const PackagingImageSection = styled.div`
    width: 56%;
    text-align: center;
    align-self: center;
    @media ${(props) => props.theme.breakpoints.mobile} {
        width: auto;
        padding-bottom: 25px;
    }
    @media ${(props) => props.theme.breakpoints.tablet} {
      width: 50%;
    }
    svg {
      @media ${(props) => props.theme.breakpoints.touch} {
        width: 94%;
      }
      @media ${(props) => props.theme.breakpoints.desktop} {
        width: 78%;
      }
    }
    svg tspan {
      @media ${(props) => props.theme.breakpoints.touch} {
        font-size: 13px;
      }
      @media ${(props) => props.theme.breakpoints.desktop} {
        font-size: 14px;
      }
    }
 `;

const setSvgContent = (el, content) => {
  if (el.innerHTML) {
    // eslint-disable-next-line no-param-reassign
    el.innerHTML = content;
  } else {
    // eslint-disable-next-line no-unused-expressions
    el.firstChild && el.removeChild(el.firstChild);
    el.appendChild(document.createTextNode(content));
  }
};

const setDomProps = (label, valueType, isBoxed, sizeInInches, translations) => {
  const boxType = isBoxed ? 'product' : 'package';
  const unitLabel = sizeInInches ? 'in' : 'mm';
  const dimensionValue = sizeInInches
    ? ((valueType * TO_INCHES) + 0.1).toFixed(1).replace('.0', '')
    : Math.ceil(valueType * TO_MILLIMETERS).toFixed(0);

  const titleEl = document.getElementById(`${boxType}-${label}-title`);
  if (titleEl) {
    setSvgContent(
      titleEl,
      `${translations[`component.packaging.${label}Label`]}:`
    );
  }
  const valueEl = document.getElementById(`${boxType}-${label}-value`);
  if (valueEl) {
    setSvgContent(
      valueEl,
      `${dimensionValue} ${unitLabel}`
    );
  }
};

const setImageDimensions = (sizeInInches, isBoxed, { width, depth, height }, translations) => {
  setDomProps('width', width, isBoxed, sizeInInches, translations);
  setDomProps('depth', depth, isBoxed, sizeInInches, translations);
  setDomProps('height', height, isBoxed, sizeInInches, translations);
};

const getDimensions = (product, box, boxed) => {
  if (product != null && box != null) {
    return boxed ? product : box;
  }

  return box != null ? box : product;
};

const ProductSizing = ({ imageBasePath, svgImageList }) => {
  const [boxed, setBoxed] = useState(false);
  const [sizeInInches, setSizeInInches] = useState(true);


  const isSwitchable = svgImageList.product != null && svgImageList.box != null;
  const dimensions = getDimensions(svgImageList.product, svgImageList.box, boxed);
  const isAlternativeContentEnabled = svgImageList['alternative-content-enabled'];
  const blockContent = isAlternativeContentEnabled
    ? svgImageList['alternative-block-content'] : svgImageList['block-content'];

  const millimetersLabel = blockContent['component.packaging.millimetersLabel'];
  const inchesLabel = blockContent['component.packaging.inchesLabel'];
  const sizingSectionTitle = blockContent['component.packaging.sizingSectionTitle'];

  const BOXED = 'show me boxed dimension link';
  const UNBOXED = 'show me unboxed dimension link';
  const INCH = 'show inches';
  const MM = 'show millimetres';

  useEffect(() => {
    if (!isSwitchable) {
      setBoxed(true);
      setImageDimensions(sizeInInches, svgImageList.box == null, dimensions, blockContent);
    } else {
      setImageDimensions(sizeInInches, boxed, dimensions, blockContent);
    }
  }, [sizeInInches, boxed, isSwitchable]);

  const linkLabel = blockContent[`component.packaging.${boxed
    ? 'packageDimensions' : 'productDimensions'}`];

  const svgData = getDimensions(svgImageList.product, svgImageList.box, boxed).image;

  return (
    <EnvironmentThemeProvider>
      <ProductSizingContainer>
        <SizeAndPackageTitle>{sizingSectionTitle}</SizeAndPackageTitle>
        {isSwitchable
          ? (
            <PackageDimensionLink onClick={() => {
              setBoxed(!boxed);
              productSizingShowMeLink(boxed ? BOXED : UNBOXED);
            }}
            >
              {linkLabel}
            </PackageDimensionLink>
          )
          : ''}
        <PackageContentSection>
          <PackagingImageSection>
            <div dangerouslySetInnerHTML={{ __html: svgData.data }} title={svgData.alt} />
            <BoxsizeButtonContainer>
              <SizeLabel
                isActive={!sizeInInches}
                onClick={() => {
                  setSizeInInches(false);
                  productSizingUnits(MM);
                }}
              >
                {millimetersLabel}
              </SizeLabel>
              <ToggleMeasurement
                label={''}
                active={sizeInInches}
                onClick={() => {
                  productSizingUnits(sizeInInches ? MM : INCH);
                  setSizeInInches(!sizeInInches);
                }}
              />
              <SizeLabel
                isActive={sizeInInches}
                onClick={() => {
                  setSizeInInches(true);
                  productSizingUnits(INCH);
                }}
              >
                {inchesLabel}
              </SizeLabel>
            </BoxsizeButtonContainer>
          </PackagingImageSection>
          <VideoSection
            translations={blockContent}
            imageBasePath={imageBasePath}
            isAlternative={isAlternativeContentEnabled}
          />
        </PackageContentSection>
      </ProductSizingContainer>
    </EnvironmentThemeProvider>
  );
};
ProductSizing.propTypes = {
  imageBasePath: PropTypes.string.isRequired,
  svgImageList: PropTypes.shape({
    product: PropTypes.oneOfType([
      PropTypes.object.isRequired,
      null
    ]),
    box: PropTypes.oneOfType([
      PropTypes.object.isRequired,
      null
    ])
  }).isRequired
};
export default ProductSizing;
